import { useState, useCallback } from "react"
import { useMutation, useQuery } from "@apollo/client"
import {
  Badge,
  Box,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import {
  GetTenantLimitsDocument,
  UpdateOrganizationLimitDocument,
  RemoveOrganizationLimitDocument,
  SetTenantLimitDocument,
} from "@hydrologiq/gql-experience/graphql"
import { UpdateButton } from "@hydrologiq/shared/content/components/Button"

interface OrganizationTenant {
  id: string
  name: string
  enabled: boolean
}

interface Organization {
  id: string
  name: string
  slug: string
  enabled: boolean
  tenants: OrganizationTenant[]
  seatLimit?: number | null
  memberCount: number
  createdAt: string
}

interface ManageLimitsDialogProps {
  organization: Organization
  onClose: () => void
  onUpdate: () => void
}

export const ManageLimitsDialog = ({ organization, onClose, onUpdate }: ManageLimitsDialogProps) => {
  const enabledTenants = organization.tenants.filter((t) => t.enabled)
  const hasMultipleTenants = enabledTenants.length > 1

  const [selectedTenantId, setSelectedTenantId] = useState<string>(enabledTenants[0]?.id || "")

  // Seat Limits State
  const [seatLimit, setSeatLimit] = useState<string>(organization.seatLimit ? organization.seatLimit.toString() : "")
  const [unlimitedSeats, setUnlimitedSeats] = useState(!organization.seatLimit)

  // Usage Limits State
  const [assetLimit, setAssetLimit] = useState<string>("")
  const [unlimitedAssets, setUnlimitedAssets] = useState(true)

  const { data: limitsData, loading: limitsLoading } = useQuery(GetTenantLimitsDocument, {
    variables: { tenantId: selectedTenantId },
    skip: !selectedTenantId,
    onCompleted: (data) => {
      const assetLimitData = data.getTenantLimits?.find((l: any) => l.resourceType === "ASSET")
      if (assetLimitData) {
        setAssetLimit(assetLimitData.limitValue.toString())
        setUnlimitedAssets(!assetLimitData.enabled)
      } else {
        setAssetLimit("")
        setUnlimitedAssets(true)
      }
    },
  })

  const [updateSeatLimit, { loading: updatingSeatLimit }] = useMutation(UpdateOrganizationLimitDocument, {
    onCompleted: () => {
      toaster.success({ title: "Seat limit updated" })
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to update seat limit", description: error.message })
    },
  })

  const [removeSeatLimit, { loading: removingSeatLimit }] = useMutation(RemoveOrganizationLimitDocument, {
    onCompleted: () => {
      toaster.success({ title: "Seat limit removed" })
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to remove seat limit", description: error.message })
    },
  })

  const [setUsageLimit, { loading: updatingUsageLimit }] = useMutation(SetTenantLimitDocument, {
    onCompleted: () => {
      toaster.success({ title: "Usage limit updated" })
    },
    onError: (error) => {
      toaster.error({ title: "Failed to update usage limit", description: error.message })
    },
  })

  const handleUpdateSeatLimit = useCallback(() => {
    if (unlimitedSeats) {
      removeSeatLimit({
        variables: {
          organizationId: organization.id,
        },
      })
    } else {
      const limit = parseInt(seatLimit, 10)
      if (isNaN(limit) || limit < 1) {
        toaster.error({ title: "Please enter a valid seat limit" })
        return
      }
      updateSeatLimit({
        variables: {
          input: {
            organizationId: organization.id,
            seatLimit: limit,
          },
        },
      })
    }
  }, [unlimitedSeats, seatLimit, organization.id, updateSeatLimit, removeSeatLimit])

  const handleUpdateUsageLimit = useCallback(() => {
    if (!selectedTenantId) {
      toaster.error({ title: "Please select a tenant" })
      return
    }

    if (unlimitedAssets) {
      // Set a very high limit or disable
      setUsageLimit({
        variables: {
          input: {
            tenant: selectedTenantId,
            resourceType: "ASSET",
            limitValue: 999999,
            enabled: false,
          },
        },
      })
    } else {
      const limit = parseInt(assetLimit, 10)
      if (isNaN(limit) || limit < 1) {
        toaster.error({ title: "Please enter a valid asset limit" })
        return
      }
      setUsageLimit({
        variables: {
          input: {
            tenant: selectedTenantId,
            resourceType: "ASSET",
            limitValue: limit,
            enabled: true,
          },
        },
      })
    }
  }, [selectedTenantId, unlimitedAssets, assetLimit, setUsageLimit])

  const tenantCollection = createListCollection({
    items: enabledTenants.map((t) => ({ label: t.name, value: t.id })),
  })

  const currentAssetUsage = limitsData?.getTenantLimits?.find((l: any) => l.resourceType === "ASSET")?.currentCount || 0

  return (
    <Stack gap={6} align="stretch" width="100%">
      {/* Tenant Selector (only if multiple tenants) */}
      {hasMultipleTenants && (
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Select Context
          </Text>
          <NativeSelect.Root>
            <NativeSelect.Field value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)}>
              {tenantCollection.items.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </NativeSelect.Field>
          </NativeSelect.Root>
          <Text fontSize="xs" mt={1}>
            Usage limits are context-specific
          </Text>
        </Box>
      )}

      {/* Seat Limits Section */}
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Seat Limits
        </Text>
        <Text fontSize="sm" mb={4}>
          Control the maximum number of members in this organisation
        </Text>

        <Box p={4} borderWidth={1} borderRadius="md" mb={4}>
          <Text fontSize="sm" mb={2}>
            Current Usage: {organization.memberCount} / {organization.seatLimit || "Unlimited"}
          </Text>
          {organization.seatLimit && organization.memberCount >= organization.seatLimit && (
            <Badge colorPalette="red" size="sm">
              At Capacity
            </Badge>
          )}
        </Box>

        <VStack align="stretch" gap={3}>
          <HStack>
            <input
              type="checkbox"
              checked={unlimitedSeats}
              onChange={(e) => setUnlimitedSeats(e.target.checked)}
              id="unlimited-seats-checkbox"
            />
            <Text fontSize="sm" as="label" htmlFor="unlimited-seats-checkbox">
              Unlimited members
            </Text>
          </HStack>
          {!unlimitedSeats && (
            <Input
              type="number"
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              placeholder="Enter seat limit"
              min="1"
            />
          )}
          <UpdateButton
            colorPalette="blue"
            context={{ label: "Update Seat Limit", description: "Update the seat limit for the selected tenant" }}
            onClick={handleUpdateSeatLimit}
            loading={updatingSeatLimit || removingSeatLimit}
          />
        </VStack>
      </Box>

      {/* Usage Limits Section */}
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Usage Limits
        </Text>
        <Text fontSize="sm" mb={4}>
          Control resource usage for this organisation
        </Text>

        {limitsLoading ? (
          <Spinner size="sm" />
        ) : (
          <>
            <Box p={4} borderWidth={1} borderRadius="md" mb={4}>
              <Text fontSize="sm" mb={2}>
                Current Asset Usage: {currentAssetUsage} / {unlimitedAssets ? "Unlimited" : assetLimit}
              </Text>
            </Box>

            <VStack align="stretch" gap={3}>
              <HStack>
                <input
                  type="checkbox"
                  checked={unlimitedAssets}
                  onChange={(e) => setUnlimitedAssets(e.target.checked)}
                  id="unlimited-assets-checkbox"
                />
                <Text fontSize="sm" as="label" htmlFor="unlimited-assets-checkbox">
                  Unlimited assets
                </Text>
              </HStack>
              {!unlimitedAssets && (
                <Input
                  type="number"
                  value={assetLimit}
                  onChange={(e) => setAssetLimit(e.target.value)}
                  placeholder="Enter asset limit"
                  min="1"
                />
              )}
              <UpdateButton
                context={{ label: "Update Usage Limit", description: "Update the usage limit for the selected tenant" }}
                onClick={handleUpdateUsageLimit}
                loading={updatingUsageLimit}
                size="sm"
              />
            </VStack>
          </>
        )}
      </Box>
    </Stack>
  )
}
